import { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Eye, EyeOff, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function apiErrorMessage(e, fallback = "Something went wrong. Please try again.") {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}

/**
 * Profile "Security" card. Two states:
 *  - user has `password` in auth_methods → inline change-password form
 *  - user is google-only            → CTA linking to /login (Sign up tab)
 */
export default function ProfileSecurity({ user }) {
  const methods = user?.auth_methods || [];
  const hasPassword = methods.includes("password");

  if (!hasPassword) {
    return (
      <section className="mt-12" data-testid="profile-security-google-only">
        <h2 className="font-heading text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3">Security</h2>
        <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-sky-50 dark:bg-sky-950/40 grid place-items-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-sky-700 dark:text-sky-300"/>
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-semibold text-slate-900 dark:text-slate-100">Add a password</div>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
                You currently sign in with Google. Add a password so you can also sign in with your email — same account, either method.
              </p>
              <Link to="/login" state={{ mode: "signup" }} data-testid="profile-add-password-link">
                <Button className="mt-4 bg-slate-900 hover:bg-slate-700 text-white">
                  Add a password <ArrowRight className="w-4 h-4 ml-2"/>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return <ChangePasswordForm />;
}

function ChangePasswordForm() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setError(""); setShow(false); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setError("New passwords don't match."); return; }
    if (next === current) { setError("New password must differ from current password."); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      const revoked = r.data?.other_sessions_revoked || 0;
      toast.success(
        revoked > 0
          ? `Password updated. ${revoked} other session${revoked === 1 ? "" : "s"} signed out.`
          : "Password updated."
      );
      setOpen(false);
      reset();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-12" data-testid="profile-security">
      <h2 className="font-heading text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3">Security</h2>
      <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center shrink-0">
            <KeyRound className="w-5 h-5 text-slate-700 dark:text-slate-300"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-heading font-semibold text-slate-900 dark:text-slate-100">Password</div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Change your password. Other sessions will be signed out.</p>
              </div>
              {!open && (
                <Button
                  variant="outline"
                  className="border-slate-300 dark:border-slate-700"
                  data-testid="profile-change-password-open"
                  onClick={() => { reset(); setOpen(true); }}
                >
                  Change password
                </Button>
              )}
            </div>

            {open && (
              <form onSubmit={submit} className="mt-5 space-y-3" data-testid="profile-change-password-form">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Current password</label>
                  <div className="relative">
                    <Input
                      data-testid="profile-current-password"
                      type={show ? "text" : "password"}
                      required
                      value={current}
                      onChange={e => setCurrent(e.target.value)}
                      autoComplete="current-password"
                      className="pr-10"
                    />
                    <button type="button" onClick={() => setShow(s => !s)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 dark:text-slate-500 hover:text-slate-700"
                            aria-label={show ? "Hide passwords" : "Show passwords"}
                            data-testid="profile-password-toggle">
                      {show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">New password</label>
                  <Input
                    data-testid="profile-new-password"
                    type={show ? "text" : "password"}
                    required
                    minLength={8}
                    value={next}
                    onChange={e => setNext(e.target.value)}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Confirm new password</label>
                  <Input
                    data-testid="profile-confirm-password"
                    type={show ? "text" : "password"}
                    required
                    minLength={8}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                {error && (
                  <div data-testid="profile-change-password-error"
                       className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
                    {error}
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <Button type="submit" disabled={busy}
                          data-testid="profile-change-password-submit"
                          className="bg-slate-900 hover:bg-slate-700 text-white">
                    {busy && <Loader2 className="w-4 h-4 animate-spin mr-2"/>}
                    Update password
                  </Button>
                  <Button type="button" variant="outline"
                          data-testid="profile-change-password-cancel"
                          onClick={() => { setOpen(false); reset(); }}
                          className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
